import { cloneDeep, sum } from 'lodash';

import {
  APISchedule,
  Appliance,
  ArticleData,
  AssignmentData,
  Bill,
  BillForecast,
  BillsStatus,
  BillUploadPayload,
  CheckoutPayload,
  CostsData,
  CurrentDynamicOperatingEnvelopeSummary,
  CurrentEnergyRates,
  EvChargerCurrentData,
  EvChargerStatusData,
  ImageWithBase64,
  ImageWithUrl,
  Investment,
  InvestmentPayload,
  Investments,
  LiveDataSummary,
  LiveWeatherData,
  Milestone,
  NotificationsToken,
  NotificationsTokenPayload,
  Retailer,
  RPCData,
  SavingsAPIData,
  SavingsAPIDataV2,
  SetChargeCurrentPayload,
  SiteDetailData,
  SiteDeviceSchedule,
  SiteDeviceScheduleToCreate,
  SiteListData,
  SiteSolarSpongeConfig,
  SiteSolarSpongeConfigWrite,
  SiteTariff,
  StorageData,
  SunriseSunsetData,
  Switch,
  SwitchSchedule,
  SwitchScheduleToCreate,
  TariffTemplate,
  TeslaConnection,
  TicketCommentAttachmentPayload,
  TicketCommentToSave,
  TicketToSave,
  Tip,
  TotalCostsData,
  UploadUrlPayload,
  UsageAPIData,
  UsageSummaryData,
  UserData,
  VersionCheckPayload,
  VersionCheckResult,
  ZendeskUser,
} from 'clipsal-cortex-types/src/api';
import { Activity } from 'clipsal-cortex-types/src/api/api-activities';
import { PaginatedResponse, PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';
import { formatDateTime } from 'clipsal-cortex-utils/src/formatting/formatting';

import { HTTPMethod, RETRIEVE_ROUTE_MAP } from './route-map';
import bill from './stubs/bill.json';
import image from './stubs/image.json';
import liveDataSummary from './stubs/live_data_summary.json';
import firstEnergyLogo from './stubs/logo_1st_energy.json';
import aglLogo from './stubs/logo_agl.json';
import amberLogo from './stubs/logo_amber.json';
import energyAusLogo from './stubs/logo_energy_australia.json';
import momentumLogo from './stubs/logo_momentum.json';
import nectrLogo from './stubs/logo_nectr.json';
import powershopLogo from './stubs/logo_powershop.json';
import redEnergyLogo from './stubs/logo_red_energy.json';
import sites from './stubs/sites.json';
import {
  getActivityForDay,
  getBillForecast,
  getCostsData,
  getSavingsData,
  getStorageData,
  getUsageData,
  getUsageSummaryData,
} from './time-series-helpers';

type OperatorPayloadTypes = Investment &
  SwitchSchedule &
  Partial<Switch> &
  SetChargeCurrentPayload &
  SiteTariff &
  SiteDeviceScheduleToCreate[] &
  SiteDeviceSchedule[] &
  SiteSolarSpongeConfigWrite;

export type OtherPayloadTypes =
  | NotificationsTokenPayload
  | TicketCommentToSave
  | TicketCommentAttachmentPayload
  | TicketToSave
  | Partial<SwitchSchedule>
  | SwitchScheduleToCreate
  | InvestmentPayload
  | BillUploadPayload
  | CheckoutPayload
  | VersionCheckPayload
  | UploadUrlPayload
  | SiteSolarSpongeConfigWrite;

export type AllResponseTypes =
  | APISchedule[]
  | Activity[]
  | Appliance[]
  | ArticleData[]
  | AssignmentData[]
  | BillForecast
  | Bill[]
  | BillsStatus
  | CostsData
  | CostsData[]
  | CurrentEnergyRates[]
  | EvChargerCurrentData
  | EvChargerStatusData
  | ImageWithBase64
  | ImageWithUrl
  | Investment
  | Investments
  | LiveDataSummary
  | LiveWeatherData
  | Milestone[]
  | Partial<Switch>
  | PaginatedResponse<SiteListData>
  | PaginatedResponseV2<Retailer>
  | PaginatedResponseV2<SiteTariff>
  | RPCData
  | SavingsAPIData[]
  | SiteDetailData
  | SiteDeviceSchedule[]
  | SiteSolarSpongeConfig
  | SiteTariff
  | StorageData
  | SunriseSunsetData
  | Switch[]
  | SwitchSchedule
  | SavingsAPIDataV2[]
  | SwitchSchedule[]
  | TariffTemplate[]
  | TeslaConnection
  | Tip[]
  | TotalCostsData
  | UsageAPIData
  | UsageSummaryData
  | UserData
  | ZendeskUser
  | VersionCheckResult
  | SiteSolarSpongeConfigWrite
  | NotificationsToken
  | CurrentDynamicOperatingEnvelopeSummary;

const RETAILER_ID_TO_LOGO_MAP: Record<number, ImageWithBase64> = {
  23: firstEnergyLogo,
  1: aglLogo,
  36: amberLogo,
  5: energyAusLogo,
  14: momentumLogo,
  35: nectrLogo,
  118: powershopLogo,
  18: redEnergyLogo,
};

type Operator = (queryParams: URLSearchParams, route: string) => AllResponseTypes | undefined;

type OperatorWithBody = (
  queryParams: URLSearchParams,
  route: string,
  body: OperatorPayloadTypes
) => AllResponseTypes | undefined;

type DynamicRouteMatchConfigurations = Record<
  HTTPMethod,
  {
    regex: RegExp;
    operator?: Operator;
    operatorWithBody?: OperatorWithBody;
  }[]
>;

const DYNAMIC_ROUTE_MATCH_CONFIGURATIONS: DynamicRouteMatchConfigurations = {
  GET: [
    {
      regex: /\/v1\/sites\?limit=\d+&offset=\d+/,
      operator: () => sites as PaginatedResponse<SiteListData>,
    },
    {
      regex: /\/v1\/sites\/\d+\/live_data_summary/,
      operator: () => ({ ...liveDataSummary, last_updated: formatDateTime(new Date()) } as LiveDataSummary),
    },
    {
      regex: /\/v1\/sites\/\d+\/sunrise_sunset/,
      operator: (queryParams: URLSearchParams) => {
        // get the date from the query params, or use today's date if not provided
        const formattedDate = queryParams.get('date') ?? new Date().toISOString().split('T')[0];

        return {
          sunrise: `${formattedDate} 06:10:58`,
          sunset: `${formattedDate} 20:32:33`,
        };
      },
    },
    {
      regex: /\/v2\/site_tariffs\/\d+/,
      operator: (_: URLSearchParams, route: string) => {
        const match = /\/v2\/site_tariffs\/(\d+)/.exec(route);
        const tariffId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'];
        const allTariffs = cloneDeep(source) as PaginatedResponseV2<SiteTariff>;
        return allTariffs.results.find((tariff) => tariff.id === tariffId);
      },
    },
    {
      regex: /\/v1\/sites\/\d+\/costs.*/,
      operator: getCostsData,
    },
    {
      regex: /\/v2\/sites\/\d+\/savings.*/,
      operator: getSavingsData,
    },
    {
      regex: /\/v1\/sites\/\d+\/bill_forecast.*/,
      operator: getBillForecast,
    },
    {
      regex: /\/v1\/sites\/\d+\/activity.*/,
      operator: getActivityForDay,
    },
    {
      regex: /\/v1\/sites\/\d+\/usage\?.*/,
      operator: getUsageData,
    },
    {
      regex: /\/v1\/sites\/\d+\/usage_summary.*/,
      operator: getUsageSummaryData,
    },
    {
      regex: /\/v1\/sites\/\d+\/storage.*/,
      operator: getStorageData,
    },
    {
      regex: /\/v1\/sites\/\d+\/image_url.*/,
      operator: () => image as ImageWithUrl,
    },
    {
      regex: /\/v1\/bills\/\d+/,
      operator: () => bill as ImageWithBase64,
    },
    {
      regex: /\/v1\/retailers\/(\d+)\/logo/,
      operator: (_: URLSearchParams, route: string) => {
        const match = /\/v1\/retailers\/(\d+)\/logo/.exec(route);
        const retailerId = Number(match![1]);
        return RETAILER_ID_TO_LOGO_MAP[retailerId in RETAILER_ID_TO_LOGO_MAP ? retailerId : 0];
      },
    },
  ],
  PATCH: [
    {
      regex: /\/v1\/sites\/1\/investments\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: Investment) => {
        const match = /\/v1\/sites\/1\/investments\/(\d+)/.exec(route);
        const investmentId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP['/v1/sites/1/investments'];
        const clonedSource = cloneDeep(source) as Investments;

        const investments = clonedSource.investments.map((investment) => {
          if (investment.id === investmentId) {
            return {
              ...investment,
              ...body,
            };
          }
          return investment;
        });
        const totalInvestment = sum(investments.map((investment) => investment.amount));
        const paybackRemaining = totalInvestment - clonedSource.total_savings;
        const clonedSourceValue: Investments = {
          ...clonedSource,
          investments,
          total_investment: totalInvestment,
          payback_remaining: paybackRemaining,
        };

        RETRIEVE_ROUTE_MAP['/v1/sites/1/investments'] = clonedSourceValue;
        return clonedSourceValue.investments.find(({ id }) => id === investmentId);
      },
    },
    {
      regex: /\/v1\/schedules\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SwitchSchedule) => {
        const match = /\/v1\/schedules\/(\d+)/.exec(route);
        const scheduleId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP[`/v1/switches/${body.switch_id}/schedules`];
        const schedules = cloneDeep(source) as SwitchSchedule[];
        const updatedSchedules = schedules.map((schedule) => {
          if (schedule.schedule_id === scheduleId) return { ...schedule, ...body };
          return schedule;
        });

        RETRIEVE_ROUTE_MAP[`/v1/switches/${body.switch_id}/schedules`] = updatedSchedules;
        const newSchedule = updatedSchedules.find((schedule) => schedule.schedule_id === scheduleId);
        return newSchedule;
      },
    },
    {
      regex: /\/v1\/switches\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: Partial<Switch>) => {
        const match = /\/v1\/switches\/(\d+)/.exec(route);
        const switchId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP['/v1/sites/1/switches'];
        const switches = cloneDeep(source) as Switch[];
        const selectedSwitch = switches.find((switchItem) => switchItem.id === switchId);
        RETRIEVE_ROUTE_MAP['/v1/sites/1/switches'] = switches.map((switchItem) => {
          if (switchItem.id === switchId) {
            return {
              ...switchItem,
              ...body,
            };
          }
          return switchItem;
        });
        return { ...selectedSwitch, scheduling: body.scheduling || selectedSwitch?.scheduling };
      },
    },
    {
      regex: /\/v1\/evs\/(\d+)\/set_charge_current/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SetChargeCurrentPayload) => {
        const match = /\/v1\/evs\/(\d+)\/set_charge_current/.exec(route);
        const evChargerId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP[`/v1/evs/${evChargerId}/status`];
        const evStatus = cloneDeep(source) as EvChargerStatusData;
        RETRIEVE_ROUTE_MAP[`/v1/evs/${evChargerId}/status`] = {
          ...evStatus,
          power: body.max_amps,
        };
        RETRIEVE_ROUTE_MAP[`/v1/evs/${evChargerId}/charge_current`] = {
          energy_unit: 'A',
          current: body.max_amps,
        };
        return evStatus;
      },
    },
  ],
  DELETE: [
    {
      regex: /\/v2\/site_tariffs\/(\d+)/,
      operator: (_: URLSearchParams, route: string) => {
        const match = /\/v2\/site_tariffs\/(\d+)/.exec(route);
        const tariffId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'];
        const clonedSource = cloneDeep(source) as PaginatedResponseV2<SiteTariff>;
        clonedSource.results = clonedSource.results.filter((tariff) => tariff.id !== tariffId);
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] = clonedSource;
        return {};
      },
    },
    {
      regex: /\/v1\/schedules\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SwitchSchedule) => {
        const match = /\/v1\/schedules\/(\d+)/.exec(route);
        const scheduleId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP[`/v1/switches/${body.switch_id}/schedules`];
        const schedules = cloneDeep(source) as SwitchSchedule[];
        RETRIEVE_ROUTE_MAP[`/v1/switches/${body.switch_id}/schedules`] = schedules.filter(
          (schedule) => schedule.schedule_id !== scheduleId
        );
        return {};
      },
    },
  ],
  POST: [
    {
      regex: /\/v1\/version_check/,
      operator: () => ({
        update_required: false,
        title: 'No Update Required',
        message: null,
      }),
    },
    {
      regex: /\/v2\/sites\/\d+\/tariffs/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SiteTariff) => {
        const newTariff = updateTariffBodyToMatchAPISchema(body);
        const source = RETRIEVE_ROUTE_MAP[route];
        const clonedSource = cloneDeep(source) as PaginatedResponseV2<SiteTariff>;
        clonedSource.results.unshift(newTariff);
        RETRIEVE_ROUTE_MAP[route] = clonedSource;
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs?offset=0&limit=5'] = clonedSource;
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] = clonedSource;
        return newTariff;
      },
    },
    {
      regex: /\/v1\/evs\/(\d+)\/stop_charge/,
      operator: () => {
        const source = RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'];
        const evStatus = cloneDeep(source) as EvChargerStatusData;
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMinutes(endDate.getMinutes() - 10);
        RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'] = {
          ...evStatus,
          status: 'Finishing',
          latest_charge_session_start: startDate.toISOString(),
          latest_charge_session_end: endDate.toISOString(),
          energy_consumed: 60,
        };

        return evStatus;
      },
    },
    {
      regex: /\/v1\/evs\/(\d+)\/pause_charge/,
      operator: () => {
        const source = RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'];
        const evStatus = cloneDeep(source) as EvChargerStatusData;
        RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'] = {
          ...evStatus,
          status: 'SuspendedEVSE',
        };

        return evStatus;
      },
    },
    {
      regex: /\/v1\/evs\/(\d+)\/start_charge/,
      operator: () => {
        const source = RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'];
        const evStatus = cloneDeep(source) as EvChargerStatusData;
        RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'] = {
          ...evStatus,
          status: 'Charging',
          control_mode: 'Manual',
        };

        return evStatus;
      },
    },
    {
      regex: /\/v1\/evs\/(\d+)\/resume_charge/,
      operator: () => {
        const source = RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'];
        const evStatus = cloneDeep(source) as EvChargerStatusData;
        RETRIEVE_ROUTE_MAP['/v1/evs/1640/status'] = {
          ...evStatus,
          status: 'Charging',
          control_mode: 'Automatic',
        };

        return evStatus;
      },
    },
    {
      regex: /\/v1\/site_devices\/(\d+)\/schedules.*/,
      operatorWithBody: (_: URLSearchParams, __: string, body: SiteDeviceScheduleToCreate[]) => {
        const responseData: SiteDeviceSchedule[] = body.map((schedule) => ({
          ...schedule,
          // this is set 0 to match the condition in the EditAllSchedules s.startScheduleId == 0
          schedule_id: 0,
        }));
        RETRIEVE_ROUTE_MAP[`/v1/site_devices/${body[0].site_device_id}/schedules`] = responseData;
        return responseData;
      },
    },
  ],
  PUT: [
    {
      regex: /\/v2\/site_tariffs\/\d+/,
      operatorWithBody: (_: URLSearchParams, __: string, body: SiteTariff) => {
        const updatedTariff = updateTariffBodyToMatchAPISchema(body);
        const source = RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs?offset=0&limit=5'];
        const clonedSource = cloneDeep(source) as PaginatedResponseV2<SiteTariff>;
        clonedSource.results.forEach((tariff) => {
          if (tariff.id === updatedTariff.id) return updatedTariff;
          return tariff;
        });
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs?offset=0&limit=5'] = clonedSource;
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] = clonedSource;
        return updatedTariff;
      },
    },
    {
      regex: /\/v1\/site_devices\/(\d+)\/schedules.*/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SiteDeviceSchedule[]) => {
        const match = /\/v1\/site_devices\/(\d+)\/schedules.*/.exec(route);
        const siteDeviceId = Number(match![1]);
        const updatedSchedules = body;
        RETRIEVE_ROUTE_MAP[`/v1/site_devices/${siteDeviceId}/schedules`] = updatedSchedules;
        return updatedSchedules;
      },
    },
    {
      regex: /\/v1\/sites\/(\d+)\/solar_sponges.*/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SiteSolarSpongeConfigWrite) => {
        const match = /\/v1\/sites\/(\d+)\/solar_sponges.*/.exec(route);
        const siteId = Number(match![1]);
        RETRIEVE_ROUTE_MAP[`/v1/sites/${siteId}/solar_sponges`] = body;
        return body;
      },
    },
  ],
};

function updateTariffBodyToMatchAPISchema(body: SiteTariff) {
  const tariffData = body;
  const source = RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs?offset=0&limit=5'] as PaginatedResponseV2<SiteTariff>;
  const currentTariffList = source.results;
  const isNewTariff = !tariffData.id;
  const id = isNewTariff ? currentTariffList.length + 1 : tariffData.id;
  return {
    ...tariffData,
    id,
    tariff: {
      id,
      ...tariffData.tariff,
      retailer: {
        id: tariffData.tariff.retailer_id ?? 0,
        name: 'Retailer Name',
        url: '',
        country_code: 'AU',
      },
      rates: tariffData.tariff.rates.map((rate, rateIndex) => ({
        ...rate,
        id: rateIndex + 1,
        season: tariffData.tariff.seasons?.find((season) => season.season_index === rate.season_index) ?? null,
      })),
    },
  };
}

/**
 * Generates a random number using the browser's crypto API.
 * This is safer than using Math.random() because Math.random() is not cryptographically secure.
 * @returns A random number between 0 and 1.
 *
 */
const getRandomNumber = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] / 2 ** 32;
};

/**
 * Abstracts the process of creating source data in the base ROUTE_MAP object according to mutation HTTP requests.
 * Note that the path provided to this method should be that of the _data source_, not that of the
 * request responsible for updating the source (which is likely different).
 *
 * @param sourcePath - The path in `ROUTE_MAP` for the source data.
 * @param newValues - The values to update.
 * @param pk - The primary key property name of the object, auto-generated here.
 */
function createSourceDataEntry(sourcePath: string, newValues?: OtherPayloadTypes, pk = 'id'): AllResponseTypes {
  const source = RETRIEVE_ROUTE_MAP[sourcePath];
  if (!source) throw new Error(`Invalid source path: path ${sourcePath} likely does not exist for method GET`);
  const clonedSource = cloneDeep(source) as AllResponseTypes;
  let newClone: AllResponseTypes;
  const newValuesObject = { ...(newValues ?? {}), [pk]: Math.round(getRandomNumber() * 99999) };
  if (Array.isArray(clonedSource)) {
    newClone = [...clonedSource, newValuesObject] as AllResponseTypes;
  } else {
    newClone = { ...clonedSource, ...newValuesObject } as AllResponseTypes;
  }
  RETRIEVE_ROUTE_MAP[sourcePath] = newClone;
  return newClone;
}

/**
 * Some routes require data retrieved dynamically (mostly the usage page), they're returned through this helper.
 *
 * @param method - The HTTP method of the request.
 * @param route - The route to check.
 * @param body - [optional] The body of a PUT/PATCH/POST request, as an object.
 * @returns The appropriate JSON data as an object/array, or false if not applicable.
 */
export function getDynamicStubDataForRoute(method: HTTPMethod, route: string, body?: OtherPayloadTypes) {
  const queryParams = new URLSearchParams(new URL('http://example.com' + route).search);

  try {
    const matchedDynamicResponse = DYNAMIC_ROUTE_MATCH_CONFIGURATIONS[method].find(({ regex }) => regex.exec(route));
    if (matchedDynamicResponse) {
      if (matchedDynamicResponse.operatorWithBody && body !== undefined) {
        return matchedDynamicResponse.operatorWithBody(queryParams, route, body as OperatorPayloadTypes);
      }
      if (matchedDynamicResponse.operator) {
        return matchedDynamicResponse.operator(queryParams, route);
      }
    }
  } catch (e) {
    console.error(e);
    return false;
  }

  if (method === 'POST') {
    try {
      // POST endpoints usually have the same route as their GET counterpart, so we rely on this
      return createSourceDataEntry(route, body);
    } catch (e) {
      console.error(e);
      return false;
    }
  }
  return false;
}
